import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce, next } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import { AppEvent } from 'webapp/enums/events';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/forecasts/data/tooltip/base';
import ForecastsDataTooltipBaseComponent from 'webapp/components/forecasts/data/tooltip/base';

import { forecastPlotlineClassNameToPoint } from 'webapp/d-chart/renderer/forecast-chart';

export default class ForecastsDataTooltipForecastComponent extends ForecastsDataTooltipBaseComponent<Args> {
	@tracked isOverflowingDataStory = false;

	seriesLineClass = forecastPlotlineClassNameToPoint;

	get arrowPosition(): string {
		return this.isOverflowingDataStory ? 'left' : 'bottom-right';
	}

	@action
	debounceReposition() {
		debounce(this, this.reposition, 20);
	}

	@action
	reposition(): void {
		next(() => {
			const plotLinePosition = this.getPlotLinePosition();
			if (!plotLinePosition) {
				this.isAllPlotlinesHidden = true;
				this.repositionForecastHistoryTooltip(0, Infinity, 0);
				return;
			}

			this.isAllPlotlinesHidden = !plotLinePosition.height && !plotLinePosition.width;

			if (this.isExplicitlyClosed) {
				// never to overflow
				this.repositionForecastHistoryTooltip(0, Infinity, 0);
				return;
			}

			// when inside a popup with transform, position:fixed is relative to the popup
			const offset = this.getContainingBlockOffset();

			const additionalPointMarginLeft = this.additionalPointMargin,
				componentHeight = this.getComponentHeight(),
				componentWidth = this.getComponentWidth(),
				pointTop =
					plotLinePosition.top -
					offset.top -
					componentHeight -
					this.additionalPointMargin,
				storyContainerPosition = this.getStoryContainerPosition(),
				storyContainerBottom =
					(storyContainerPosition?.top || 0) + (storyContainerPosition?.height || 0);

			let left = plotLinePosition.left - offset.left - componentWidth / 2,
				top = pointTop;

			const isOverflowingDataStory =
				!!storyContainerPosition &&
				storyContainerPosition.left + storyContainerPosition.width >
					left + offset.left + componentWidth &&
				storyContainerBottom > pointTop + offset.top - componentHeight;

			this.isOverflowingDataStory = isOverflowingDataStory;

			let extraLeftPadding = 0;
			if (isOverflowingDataStory) {
				left = plotLinePosition.left - offset.left + additionalPointMarginLeft;
				top = plotLinePosition.top - offset.top - componentHeight / 2;
			} else {
				// for centering tooltip arrow on the plot line - only if at the right
				extraLeftPadding = -55;
			}

			this.top = top;
			this.left = left + extraLeftPadding;

			this.repositionForecastHistoryTooltip(
				this.top + componentHeight,
				this.left,
				this.left + componentWidth,
			);
		});
	}

	private scrollContainer: Element | null = null;

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);

		this.eventManagerService.on(AppEvent.FORECAST_CHART_RENDERED, this, this.reposition);

		// listen on the actual scrollable container, not document
		this.scrollContainer = node.closest('.metric-details__content');
		if (this.scrollContainer) {
			this.scrollContainer.addEventListener('scroll', this.debounceReposition);
		}
		document.addEventListener('scroll', this.debounceReposition);
		window.addEventListener('resize', this.debounceReposition);
	}

	@action
	teardownView(): void {
		this.eventManagerService.off(AppEvent.FORECAST_CHART_RENDERED, this);

		if (this.scrollContainer) {
			this.scrollContainer.removeEventListener('scroll', this.debounceReposition);
		}
		document.removeEventListener('scroll', this.debounceReposition);
		window.removeEventListener('resize', this.debounceReposition);
	}

	private repositionForecastHistoryTooltip(
		topLimit: number,
		leftLimit: number,
		rightLimit: number,
	) {
		this.eventManagerService.trigger(AppEvent.REPOSITION_FORECAST_HISTORY_TOOLTIP, {
			topLimit,
			leftLimit,
			rightLimit,
			isAllPlotlinesHidden: this.isAllPlotlinesHidden,
		});
	}
	<template>
		<div
			class="forecast-value-tooltip {{unless this.isVisible 'hidden'}}"
			style={{this.style}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<div class="highcharts-dbx-tooltip highcharts-dbx-tooltip--forecast">
				<div class="top">
					<div class="tooltip-title">{{this.timeLabel}}</div>
				</div>
				<div class="point-ct point-ct--secondary">
					<div>
						<div class="tooltip-title color-fg">Forecast</div>
					</div>
					<span class="value">{{@model.valueStr}}</span>
				</div>
			</div>
			<div class="arrow arrow--{{this.arrowPosition}}"></div>

			<a
				role="button"
				class="close"
				{{on "click" this.handleCloseClick}}
			><CommonUiIconComponent @name="Close" @size="sm" />
			</a>

		</div>
	</template>
}
